import type { AffineEditorContainer } from '@affine/core/blocksuite/block-suite-editor';
import type { DocsService } from '@affine/core/modules/doc';
import type { TagService } from '@affine/core/modules/tag';
import type { WorkbenchService } from '@affine/core/modules/workbench';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import { type Store, Text } from '@blocksuite/affine/store';

import { type CuriesciousActionDetail, NOTES_CHANGED_EVENT } from '../types';
import { applyNotesLink, removeNotesLink } from './notes-link-format';

/**
 * Three-state review status. Drives the "you've reviewed this many,
 * N still to look at" stats pill on the Browse page — the antidote
 * to the "endless backlog" failure mode researchers consistently
 * cite when abandoning notes tools.
 *
 *   inbox     — freshly clipped, not yet processed by the researcher
 *   reviewed  — user has read + decided to keep, but hasn't synthesised
 *   permanent — promoted into a standalone atomic note (Phase 3)
 */
export type NotesEntryStatus = 'inbox' | 'reviewed' | 'permanent';

/**
 * Structured index entry persisted on the Notes doc's
 * `custom:notesIndex` property. The body blocks remain the user-
 * editable source of truth for content; this index is the workspace-
 * wide lookup that powers the inline "Saved to Notes" panel + the
 * source-side marker round-trip. Mirrors the `branchMeta` model so
 * the same patterns transfer.
 *
 * Metadata fields (tags, theme, status, userNote, promotedToDocId)
 * are all OPTIONAL — entries created before Phase 2 land here with
 * undefined values and degrade gracefully (status defaults to
 * `inbox`, tags to `[]`, etc.).
 */
export interface NotesIndexEntry {
  /** Divider block id — also the URL anchor for jump-to-entry. */
  entryId: string;
  /** Source doc the passage was clipped from. */
  sourceDocId?: string;
  /** Source block id (may be empty for multi-block selections). */
  sourceBlockId?: string;
  /** Inline range within the source block. */
  sourceRange?: { index: number; length: number };
  /** Verbatim passage at save-time (capped, durable). */
  sourcePassage: string;
  /** ISO timestamp. */
  createdAt: string;
  /** Free-form per-entry tags (e.g. "ribosome", "DKC1", "open question"). */
  tags?: string[];
  /** Optional theme — broader bucket the researcher slots the entry under. */
  theme?: string;
  /** Review status — defaults to `inbox` when missing. */
  status?: NotesEntryStatus;
  /** Researcher's short commentary on the clipping. */
  userNote?: string;
  /** Doc id of the Permanent Note this fleeting entry was promoted to (Phase 3). */
  promotedToDocId?: string;
}

const NOTES_INDEX_PROPERTY = 'notesIndex';
const PASSAGE_CAP = 240;

function fireNotesChanged() {
  try {
    window.dispatchEvent(new CustomEvent(NOTES_CHANGED_EVENT));
  } catch {
    /* SSR / detached host — no-op */
  }
}

/**
 * Read the workspace's notes index from the Notes doc's
 * `custom:notesIndex` JSON property. Tolerant of missing / malformed
 * data — returns `[]` rather than throwing so callers can render an
 * empty state without a try/catch.
 */
export function readNotesIndex(
  docsService: DocsService,
  notesDocId: string
): NotesIndexEntry[] {
  try {
    const record = docsService.list.doc$(notesDocId).value;
    const raw = record?.getProperties()['custom:notesIndex'];
    if (typeof raw !== 'string' || !raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? (parsed as NotesIndexEntry[]) : [];
  } catch (err) {
    console.warn('[add-to-notes] index read failed', err);
    return [];
  }
}

/**
 * Overwrite the workspace's notes index. The caller is responsible
 * for ordering (newest-first is the convention everywhere we read
 * this property).
 */
export function writeNotesIndex(
  docsService: DocsService,
  notesDocId: string,
  entries: NotesIndexEntry[]
): void {
  try {
    const record = docsService.list.doc$(notesDocId).value;
    record?.setCustomProperty(NOTES_INDEX_PROPERTY, JSON.stringify(entries));
  } catch (err) {
    console.warn('[add-to-notes] index write failed', err);
  }
}

/**
 * Patch a single entry in the workspace's notes index. The patch is
 * shallow-merged onto the existing entry — pass `undefined` for a
 * field to clear it. Fires `NOTES_CHANGED_EVENT` so the Browse page
 * + inline panel refresh immediately.
 *
 * No-op when the entry isn't found (e.g. another tab deleted it
 * between the user clicking and the click landing).
 */
export function updateNotesEntry(
  docsService: DocsService,
  notesDocId: string,
  entryId: string,
  patch: Partial<Omit<NotesIndexEntry, 'entryId'>>
): void {
  try {
    const existing = readNotesIndex(docsService, notesDocId);
    let touched = false;
    const next = existing.map(e => {
      if (e.entryId !== entryId) return e;
      touched = true;
      return { ...e, ...patch } as NotesIndexEntry;
    });
    if (!touched) return;
    writeNotesIndex(docsService, notesDocId, next);
    fireNotesChanged();
  } catch (err) {
    console.warn('[add-to-notes] updateNotesEntry failed', err);
  }
}

export interface DeleteNotesEntryOptions {
  docsService: DocsService;
  notesDocId: string;
  entryId: string;
  /**
   * Known block ids belonging to the entry (in order: divider,
   * attribution, quote, empty). Passed by the toast-undo path which
   * captured them at create-time. Browse-page deletes omit this and
   * resolve the four ids from the divider on the fly.
   */
  insertedBlockIds?: string[];
  /**
   * Live source-doc store, when the caller already has it (toast-undo
   * path while the source doc is still the active editor). Lets us
   * clear the amber highlight synchronously.
   */
  sourceDoc?: Store;
  /**
   * Source doc id — used by callers that don't already hold the
   * Store instance (Browse page). We open the doc just long enough
   * to clear the inline format and release.
   */
  sourceDocId?: string;
  sourceBlockId?: string;
  sourceRange?: { index: number; length: number };
}

/**
 * Single durable deletion path for one notes entry. The toolbar's
 * Toast → Undo and the Browse-page row delete both call this — there
 * is no other way an entry should be removed. Order:
 *
 *   1. Clear the source-side amber highlight (sync if `sourceDoc`
 *      passed, otherwise async via `docsService.open(sourceDocId)`).
 *   2. Splice the index entry (the workspace-visible truth).
 *   3. Delete the four blocks in the accumulator.
 *   4. Fire NOTES_CHANGED_EVENT so subscribers refresh immediately.
 *
 * Each step is independent: a failure in one (e.g. the source block
 * has been deleted by the user) is logged but does not prevent the
 * later steps.
 */
export function deleteNotesEntry(opts: DeleteNotesEntryOptions): void {
  const {
    docsService,
    notesDocId,
    entryId,
    insertedBlockIds,
    sourceDoc,
    sourceDocId,
    sourceBlockId,
    sourceRange,
  } = opts;

  if (sourceBlockId && sourceRange) {
    if (sourceDoc) {
      try {
        removeNotesLink(sourceDoc, sourceBlockId, sourceRange);
      } catch (err) {
        console.warn('[add-to-notes] delete: source marker clear failed', err);
      }
    } else if (sourceDocId) {
      clearSourceLinkAsync(
        docsService,
        sourceDocId,
        sourceBlockId,
        sourceRange
      ).catch(err => {
        console.warn(
          '[add-to-notes] delete: async source marker clear failed',
          err
        );
      });
    }
  }

  try {
    const remaining = readNotesIndex(docsService, notesDocId).filter(
      e => e.entryId !== entryId
    );
    writeNotesIndex(docsService, notesDocId, remaining);
  } catch (err) {
    console.warn('[add-to-notes] delete: index splice failed', err);
  }

  // Toast-undo passes the captured ids; Browse-page deletes resolve
  // the four ids from the divider (entryId IS the divider id — the
  // attribution, quote, and empty paragraph follow it in order).
  if (insertedBlockIds && insertedBlockIds.length > 0) {
    deletePrependedBlocks(docsService, notesDocId, insertedBlockIds).catch(
      err => {
        console.warn('[add-to-notes] delete: block delete failed', err);
      }
    );
  } else {
    deleteEntryBlocksFromDivider(docsService, notesDocId, entryId).catch(
      err => {
        console.warn(
          '[add-to-notes] delete: divider-based block delete failed',
          err
        );
      }
    );
  }

  fireNotesChanged();
}

/**
 * Workspace tag identifying the Notes accumulator. Tag-based lookup
 * lets the user rename / move the doc without losing the linkage —
 * whichever doc carries this tag becomes the target.
 */
const NOTES_TAG_LABEL = 'Notes';
const NOTES_TAG_COLOR = '#D97706'; // warm amber — distinct from Branch teal
const NOTES_DOC_TITLE = 'Research Notes';

export interface AddToNotesOptions {
  workspace: WorkspaceService;
  docsService: DocsService;
  tagService: TagService;
  workbench: WorkbenchService;
  editor: AffineEditorContainer | null;
  detail: CuriesciousActionDetail;
}

export interface AddToNotesResult {
  notesDocId: string;
  /** True when the notes doc was created by THIS call (vs. reusing an existing one). */
  notesDocCreatedNow: boolean;
  /** Ids of the blocks we prepended — used by undo to surgically remove the entry. */
  insertedBlockIds: string[];
  /** Removes the prepended entry; also deletes the notes doc if we just created it. */
  undo: () => void;
  /** Re-opens the notes doc beside the source (used by the toast Open button). */
  openBeside: () => void;
}

function ensureNotesTag(
  tagService: TagService
): { id: string; tag: (docId: string) => void } | null {
  try {
    const existing = tagService.tagList.tags$.value.find(
      t => t.value$.value === NOTES_TAG_LABEL
    );
    if (existing) {
      return { id: existing.id, tag: (id: string) => existing.tag(id) };
    }
    const created = tagService.tagList.createTag(
      NOTES_TAG_LABEL,
      NOTES_TAG_COLOR
    );
    return { id: created.id, tag: (id: string) => created.tag(id) };
  } catch (err) {
    console.warn('[add-to-notes] tag creation failed', err);
    return null;
  }
}

/**
 * Find the doc currently designated as "the Notes doc" in this
 * workspace. Tag-based: whichever doc carries the `Notes` tag wins.
 * If multiple docs carry the tag (e.g. the user manually applied it
 * to other docs), we pick the first by creation time — Phase 2 can
 * surface a "which one" picker if it becomes an issue.
 */
function findNotesDocByTag(tagService: TagService): string | null {
  try {
    const notesTag = tagService.tagList.tags$.value.find(
      t => t.value$.value === NOTES_TAG_LABEL
    );
    if (!notesTag) return null;
    // `Tag.pageIds$` is a LiveData of the tagged doc ids — read it
    // bracket-style so the rxjs/finnish lint (which scans for
    // $-suffixed identifiers on non-Observable types) doesn't flag
    // our inline cast.
    const tagRecord = notesTag as unknown as Record<
      string,
      { value: ReadonlyArray<string> } | undefined
    >;
    const tagged = tagRecord['pageIds$']?.value;
    if (!tagged || tagged.length === 0) return null;
    return tagged[0] ?? null;
  } catch (err) {
    console.warn('[add-to-notes] tag lookup failed', err);
    return null;
  }
}

/**
 * Append to the user's Research Notes — a single, workspace-level
 * accumulator doc that captures clippings for later manuscript
 * writing. Entries are prepended (newest-first) so recent captures
 * are always at the top of the doc.
 *
 * Each entry is a 4-block sequence: divider · timestamp+source line ·
 * quote · empty paragraph (cursor lands here for the user's
 * commentary). The blocks are returned so undo can remove exactly
 * what was added.
 */
export async function addToNotes(
  opts: AddToNotesOptions
): Promise<AddToNotesResult> {
  const { workspace, docsService, tagService, workbench, editor, detail } =
    opts;

  if (!editor?.host?.std?.store) {
    throw new Error('No active editor — open a doc before adding to notes.');
  }
  const sourceDoc = editor.host.std.store;
  const sourceDocId = (sourceDoc as { id?: string }).id;
  const sourceTitle = (
    sourceDoc as { meta?: { title?: string } }
  ).meta?.title?.trim();

  // 1. Resolve-or-create the Notes doc.
  let notesDocId = findNotesDocByTag(tagService);
  let notesDocCreatedNow = false;

  if (!notesDocId) {
    const created = docsService.createDoc({ primaryMode: 'page' });
    notesDocId = created.id;
    notesDocCreatedNow = true;
    try {
      await docsService.changeDocTitle(notesDocId, NOTES_DOC_TITLE);
    } catch (err) {
      console.warn('[add-to-notes] failed to set notes-doc title', err);
    }
    const tag = ensureNotesTag(tagService);
    if (tag) {
      try {
        tag.tag(notesDocId);
      } catch (err) {
        console.warn('[add-to-notes] failed to apply Notes tag', err);
      }
    }
  } else {
    // Make sure the tag exists even if we found the doc by other means.
    ensureNotesTag(tagService);
  }

  // 2. Prepend the new entry into the notes doc's main note block.
  // First inserted id is the divider — that's the durable anchor
  // for jump-to-entry links (the user can rewrite the quote or
  // attribution text, but the divider stays).
  const insertedBlockIds = await prependNoteEntry(docsService, notesDocId, {
    passage: detail.text,
    sourceDocId,
    sourceTitle,
    timestamp: new Date(),
  });
  const entryId = insertedBlockIds[0];
  void workspace; // kept on the API for future cross-workspace work.

  // 3. Persist the structured index entry on the Notes doc so the
  // inline panel can answer "which clippings are from THIS doc?"
  // without scanning block bodies. Append-newest-first to match the
  // body order.
  if (entryId) {
    const existing = readNotesIndex(docsService, notesDocId);
    const entry: NotesIndexEntry = {
      entryId,
      sourceDocId,
      sourceBlockId: detail.sourceBlockId,
      sourceRange: detail.sourceRange,
      sourcePassage: detail.text.slice(0, PASSAGE_CAP),
      createdAt: new Date().toISOString(),
    };
    writeNotesIndex(docsService, notesDocId, [entry, ...existing]);
  }

  // 4. Paint the source-side marker — amber tint + click-through to
  // the entry's divider — so the researcher sees at a glance that
  // this passage has been clipped, and can jump back to it.
  if (entryId && detail.sourceBlockId && detail.sourceRange) {
    applyNotesLink(
      sourceDoc as unknown as Store,
      detail.sourceBlockId,
      detail.sourceRange,
      notesDocId,
      entryId
    );
  }

  fireNotesChanged();

  // 5. Open helper — used by the toast "Open Notes" button.
  const openBeside = () => {
    try {
      workbench.workbench.openDoc(notesDocId!, { at: 'beside' });
    } catch {
      try {
        workbench.workbench.openDoc(notesDocId!);
      } catch {
        /* both failed — user can still open from the sidebar */
      }
    }
  };

  // 6. Undo: delegate to the durable single-path delete helper, then
  // (if we created the notes doc this call) trash it too. Keeping
  // the entry-delete in one place means the Browse-page row delete
  // can call the same helper without re-implementing the cleanup.
  const undo = () => {
    if (entryId) {
      deleteNotesEntry({
        docsService,
        notesDocId: notesDocId!,
        entryId,
        insertedBlockIds,
        sourceDoc: sourceDoc as unknown as Store,
        sourceBlockId: detail.sourceBlockId,
        sourceRange: detail.sourceRange,
      });
    }
    if (notesDocCreatedNow) {
      try {
        (
          docsService as unknown as {
            list: { setTrash: (id: string, trash: boolean) => void };
          }
        ).list.setTrash(notesDocId!, true);
      } catch (err) {
        console.warn('[add-to-notes] undo: notes-doc trash failed', err);
      }
    }
  };

  return {
    notesDocId: notesDocId!,
    notesDocCreatedNow,
    insertedBlockIds,
    undo,
    openBeside,
  };
}

interface PrependEntryOpts {
  passage: string;
  sourceDocId?: string;
  sourceTitle?: string;
  timestamp: Date;
}

type OpenedDoc = {
  doc: {
    waitForSyncReady: () => Promise<void>;
    addPriorityLoad: (n: number) => () => void;
    blockSuiteDoc: {
      getBlock: (id: string) => { model?: unknown } | undefined;
      getBlocksByFlavour: (
        flavour: string
      ) => Array<{ id: string; model: unknown }>;
      addBlock: (
        flavour: string,
        props: Record<string, unknown>,
        parentId?: string,
        index?: number
      ) => string;
      deleteBlock: (model: unknown) => void;
      transact: (fn: () => void) => void;
      readonly?: boolean;
    };
  };
  release: () => void;
};

async function openDoc(
  docsService: DocsService,
  docId: string
): Promise<OpenedDoc> {
  return (
    docsService as unknown as { open: (id: string) => OpenedDoc }
  ).open(docId);
}

/**
 * Prepend a 4-block entry (divider · attribution · quote · empty
 * paragraph) at the top of the notes doc's main note block. Returns
 * the ids in insertion order so undo can remove them. Uses the
 * docsService.open() pattern that AffineOS uses internally for
 * mutating non-active docs — so we wait for sync before writing and
 * always release after.
 */
async function prependNoteEntry(
  docsService: DocsService,
  notesDocId: string,
  opts: PrependEntryOpts
): Promise<string[]> {
  const opened = await openDoc(docsService, notesDocId);
  const dispose = opened.doc.addPriorityLoad(10);
  const inserted: string[] = [];
  try {
    await opened.doc.waitForSyncReady();
    dispose();
    const bsDoc = opened.doc.blockSuiteDoc;
    // Bypass any user-readonly state for the writes.
    const wasReadonly = bsDoc.readonly === true;
    if (wasReadonly) bsDoc.readonly = false;
    try {
      const notes = bsDoc.getBlocksByFlavour('affine:note');
      const noteId = notes[0]?.id;
      if (!noteId) {
        console.warn(
          '[add-to-notes] notes doc has no affine:note block — skipping'
        );
        return inserted;
      }
      const attribution = formatAttribution(opts);
      const passage = opts.passage.replace(/\s+/g, ' ').trim();
      bsDoc.transact(() => {
        // Insert in REVERSE order at index 0 so the final visual
        // order is divider → attribution → quote → empty paragraph
        // (newest entry at the top of the doc).
        //
        // The `text` prop on affine:paragraph is a Y.Text in the
        // BlockSuite schema — plain strings are silently stored but
        // never render. Wrap each in `new Text(...)` so the
        // attribution + quote actually show up in the doc body.
        const emptyId = bsDoc.addBlock('affine:paragraph', {}, noteId, 0);
        const quoteId = bsDoc.addBlock(
          'affine:paragraph',
          { type: 'quote', text: new Text(passage) },
          noteId,
          0
        );
        const attribId = bsDoc.addBlock(
          'affine:paragraph',
          { type: 'text', text: new Text(attribution) },
          noteId,
          0
        );
        const dividerId = bsDoc.addBlock('affine:divider', {}, noteId, 0);
        inserted.push(dividerId, attribId, quoteId, emptyId);
      });
    } finally {
      if (wasReadonly) bsDoc.readonly = true;
    }
  } catch (err) {
    console.warn('[add-to-notes] prepend failed', err);
  } finally {
    opened.release();
  }
  return inserted;
}

/**
 * Browse-page delete path: we only have the divider block id (the
 * entryId). Open the notes doc, look up the divider, walk three
 * siblings forward (attribution · quote · empty paragraph — the
 * shape every entry has), and delete all four in one transaction.
 * Tolerant of partial entries (e.g. the user manually deleted the
 * empty paragraph): only deletes blocks we actually find.
 */
async function deleteEntryBlocksFromDivider(
  docsService: DocsService,
  notesDocId: string,
  dividerBlockId: string
): Promise<void> {
  let opened: OpenedDoc | null = null;
  try {
    opened = await openDoc(docsService, notesDocId);
    await opened.doc.waitForSyncReady();
    const bsDoc = opened.doc.blockSuiteDoc;
    const wasReadonly = bsDoc.readonly === true;
    if (wasReadonly) bsDoc.readonly = false;
    try {
      // The divider's siblings (attribution · quote · empty para) all
      // live under the same affine:note. We resolve the parent and
      // walk children from the divider's index +0, +1, +2, +3.
      const dividerBlock = bsDoc.getBlock(dividerBlockId);
      const dividerModel = dividerBlock?.model as
        | {
            parent?: { children?: Array<{ id: string }> };
            id?: string;
          }
        | undefined;
      const parentChildren = dividerModel?.parent?.children;
      if (!parentChildren) {
        console.warn(
          '[add-to-notes] delete: divider has no parent — skipping'
        );
        return;
      }
      const startIdx = parentChildren.findIndex(c => c.id === dividerBlockId);
      if (startIdx < 0) {
        console.warn(
          '[add-to-notes] delete: divider not found in parent — skipping'
        );
        return;
      }
      const ids: string[] = [];
      for (let i = startIdx; i < Math.min(startIdx + 4, parentChildren.length); i++) {
        const child = parentChildren[i];
        if (child) ids.push(child.id);
      }
      bsDoc.transact(() => {
        for (const id of ids) {
          try {
            const model = bsDoc.getBlock(id)?.model;
            if (model) bsDoc.deleteBlock(model);
          } catch {
            /* already gone */
          }
        }
      });
    } finally {
      if (wasReadonly) bsDoc.readonly = true;
    }
  } catch (err) {
    console.warn('[add-to-notes] divider-based delete failed', err);
  } finally {
    opened?.release();
  }
}

/**
 * Async source-marker clear for Browse-page deletes (the source doc
 * may not be open). Loads the source doc just long enough to clear
 * the inline format, then releases. Fire-and-forget; failures are
 * logged but don't block the rest of the delete pipeline.
 */
async function clearSourceLinkAsync(
  docsService: DocsService,
  sourceDocId: string,
  sourceBlockId: string,
  range: { index: number; length: number }
): Promise<void> {
  let opened: OpenedDoc | null = null;
  try {
    opened = await openDoc(docsService, sourceDocId);
    await opened.doc.waitForSyncReady();
    const bsDoc = opened.doc.blockSuiteDoc as unknown as Store;
    removeNotesLink(bsDoc, sourceBlockId, range);
  } catch (err) {
    console.warn('[add-to-notes] async source-marker clear failed', err);
  } finally {
    opened?.release();
  }
}

/**
 * Surgical undo: open the notes doc and delete exactly the blocks
 * we prepended. Run in a transaction so the deletion is atomic.
 */
async function deletePrependedBlocks(
  docsService: DocsService,
  notesDocId: string,
  blockIds: string[]
): Promise<void> {
  if (blockIds.length === 0) return;
  let opened: OpenedDoc | null = null;
  try {
    opened = await openDoc(docsService, notesDocId);
    await opened.doc.waitForSyncReady();
    const bsDoc = opened.doc.blockSuiteDoc;
    const wasReadonly = bsDoc.readonly === true;
    if (wasReadonly) bsDoc.readonly = false;
    try {
      bsDoc.transact(() => {
        for (const id of blockIds) {
          try {
            const model = bsDoc.getBlock(id)?.model;
            if (model) bsDoc.deleteBlock(model);
          } catch {
            /* already gone */
          }
        }
      });
    } finally {
      if (wasReadonly) bsDoc.readonly = true;
    }
  } catch (err) {
    console.warn('[add-to-notes] undo: block delete failed', err);
  } finally {
    opened?.release();
  }
}

function formatAttribution(opts: PrependEntryOpts): string {
  const date = opts.timestamp;
  const day = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const sourceLabel = opts.sourceTitle?.trim() || 'untitled doc';
  return `${day} · ${time} · from ${sourceLabel}`;
}
